import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { Link } from 'react-router-dom';

const ContractorsPage = () => {
  const services = [
    {
      title: 'Business Insurance',
      description:
        'Comprehensive insurance programs designed specifically for contractors in Bucks County, Montgomery County, and throughout Pennsylvania. We analyze your operations and develop tailored risk management solutions.',
      features: ['General Liability', 'Workers Compensation', 'Commercial Auto', 'Equipment Coverage', 'Umbrella Policies'],
      icon: 'ri-shield-check-line',
    },
    {
      title: 'Surety Bonding',
      description:
        'Expert surety bond services to help Pennsylvania contractors secure contracts and grow their business. Our creative underwriting approach maximizes your bonding capacity.',
      features: ['Bid Bonds', 'Performance Bonds', 'Payment Bonds', 'Maintenance Bonds', 'Supply Bonds'],
      icon: 'ri-file-shield-line',
    },
    {
      title: 'Union Bonds',
      description:
        'Streamlined union bonding process with established relationships with major unions in the Philadelphia and Bucks County area. Quick and efficient service.',
      features: ['Union Welfare Bonds', 'Union Pension Bonds', 'ERISA Bonds', 'Fast Processing', 'Expert Guidance'],
      icon: 'ri-team-line',
    },
  ];

  const industries = [
    'General Contractors', 'Electrical Contractors', 'Plumbing Contractors', 'HVAC Contractors',
    'Roofing Contractors', 'Concrete Contractors', 'Excavation Contractors', 'Masonry Contractors',
    'Steel Erection', 'Carpentry', 'Painting Contractors', 'Specialty Trades',
  ];

  return (
    <div className="min-h-screen bg-slate-900">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-green-400 text-sm font-semibold tracking-widest uppercase mb-4">Bucks County &amp; Montgomery County PA</p>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Contractor Services
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Specialized construction insurance and surety bonding solutions for Pennsylvania contractors
          </p>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Our Services for Contractors
            </h2>
            <div className="w-24 h-1 bg-green-500 mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-slate-800 border border-slate-700 rounded-2xl p-8 hover:border-green-500/50 hover:shadow-xl hover:shadow-green-900/20 transition-all duration-300 hover:-translate-y-1"
              >
                <div className="w-16 h-16 bg-green-500/10 border border-green-500/20 rounded-xl flex items-center justify-center mb-6">
                  <i className={`${service.icon} text-3xl text-green-400`}></i>
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">{service.title}</h3>
                <p className="text-slate-400 leading-relaxed mb-6">{service.description}</p>
                <ul className="space-y-3">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-slate-300">
                      <i className="ri-check-line text-green-400 mr-3 text-xl"></i>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-green-400 text-sm font-semibold tracking-widest uppercase mb-3">Why Choose Us</p>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Why Bucks &amp; Montgomery County Contractors Choose Us
              </h2>
              <div className="space-y-6">
                {[
                  { icon: 'ri-time-line', title: 'Fast Turnaround', desc: 'Quick response times and efficient processing to keep your Pennsylvania construction projects moving forward.' },
                  { icon: 'ri-user-star-line', title: 'Expert Knowledge', desc: 'Deep understanding of construction industry risks and insurance needs across Bucks County and Montgomery County.' },
                  { icon: 'ri-building-line', title: '100+ Markets', desc: 'Access to over 100 specialized insurers for competitive pricing and comprehensive coverage.' },
                  { icon: 'ri-hand-heart-line', title: 'Personal Service', desc: 'Family-owned business with a commitment to building lasting relationships with Pennsylvania contractors.' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start">
                    <div className="w-12 h-12 bg-green-500/10 border border-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0 mr-4">
                      <i className={`${item.icon} text-2xl text-green-400`}></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
                      <p className="text-slate-400">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-slate-900 border border-slate-700 rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-white mb-6">Industries We Serve in PA</h3>
              <div className="grid grid-cols-2 gap-4">
                {industries.map((industry, index) => (
                  <div
                    key={index}
                    className="flex items-center text-slate-300 bg-slate-800 border border-slate-700 rounded-lg p-3"
                  >
                    <i className="ri-hammer-line text-green-400 mr-3"></i>
                    <span className="text-sm font-medium">{industry}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-green-700 to-green-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Protect Your Business?
          </h2>
          <p className="text-xl text-white/80 mb-10">
            Get a customized construction insurance and surety bonding quote for your Bucks County or Montgomery County contracting business
          </p>
          <Link
            to="/contact"
            className="inline-block px-10 py-4 bg-white text-green-700 rounded-full font-semibold text-lg hover:bg-gray-100 transition-all shadow-lg whitespace-nowrap"
          >
            Request a Quote
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContractorsPage;
